/**
 * One adjustable pixel dimension of the schedule layout, persisted per
 * briefing AND per scope.
 *
 * The schedule has several of these — the label-column width, and the height
 * of each stacked panel — and they all share the same contract: clamp to a
 * sensible range on the way in and out, `null` means "never adjusted" so the
 * caller's own default applies, resetting removes the key, and a briefing
 * without an id (a production that isn't saved yet) stores nothing. Only the
 * key prefix and the allowed range differ per dimension, so that is all a
 * `ScheduleDimension` carries.
 *
 * Storage is per-browser UI state in localStorage via the repo's safeStorage
 * wrapper, deliberately: it also has to work on the unauthenticated
 * client-share page, and a laptop and a 4K monitor want different sizes, so
 * syncing this across devices would be wrong.
 */

import { safeLocalStorage, type StorageLike } from "@/lib/safeStorage";

export interface ScheduleDimension {
  /**
   * localStorage key segment. Changing it orphans every stored value, so
   * treat it as a stable identifier rather than a label.
   */
  keyPrefix: string;
  min: number;
  max: number;
}

/** Clamp to a range, rounding to whole pixels and rejecting NaN/Infinity. */
export function clampToRange(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function clampDimension(dimension: ScheduleDimension, value: number): number {
  return clampToRange(value, dimension.min, dimension.max);
}

const dimensionKey = (dimension: ScheduleDimension, briefingId: string, scope: string) =>
  `produce.${dimension.keyPrefix}.${scope}.${briefingId}`;

/** Returns null when this dimension was never adjusted for this briefing. */
export function loadDimension(
  dimension: ScheduleDimension,
  briefingId: string,
  scope: string,
  storage: StorageLike = safeLocalStorage,
): number | null {
  if (!briefingId) return null;
  const raw = storage.getItem(dimensionKey(dimension, briefingId, scope));
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? clampDimension(dimension, value) : null;
}

/** Passing `null` resets the dimension back to the caller's default. */
export function saveDimension(
  dimension: ScheduleDimension,
  briefingId: string,
  scope: string,
  value: number | null,
  storage: StorageLike = safeLocalStorage,
): void {
  if (!briefingId) return;
  const key = dimensionKey(dimension, briefingId, scope);
  if (value === null) {
    storage.removeItem(key);
  } else {
    storage.setItem(key, String(clampDimension(dimension, value)));
  }
}
